import React, { useState, useCallback } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Eye, EyeOff, Languages } from 'lucide-react';

/**
 * Reusable "Show English Meaning / Hide English Meaning" toggle.
 * Two usage patterns:
 *
 * 1) BLOCK — wraps a whole passage:
 *    <MeaningBlock fr="Je prends le train." en="I take the train." />
 *
 * 2) LIST — vocabulary lists with a single global toggle button:
 *    <VocabularyMeaningList
 *      items={[{ fr: 'Bonjour', en: 'Hello' }, ...]}
 *    />
 *
 * The global preference is stored in LanguageContext (persists across pages).
 * Individual instances can override with their own local button (see MeaningToggleButton).
 */

/** Standalone toggle button (uses the global showMeanings preference). */
export function MeaningToggleButton({ className = '', size = 'md', labelFr, labelEn, testid = 'meaning-toggle' }) {
  const { showMeanings, toggleShowMeanings, lang } = useLang();
  const Icon = showMeanings ? EyeOff : Eye;
  const label = showMeanings
    ? (labelEn && lang === 'en' ? labelEn.hide : lang === 'en' ? 'Hide English Meaning' : 'Masquer la traduction')
    : (labelFr && lang === 'fr' ? labelFr.show : lang === 'en' ? 'Show English Meaning' : 'Voir la traduction');

  const sizeClasses = size === 'sm'
    ? 'text-[11px] px-3 py-1.5'
    : 'text-[12px] px-4 py-2';

  return (
    <button
      type="button"
      onClick={toggleShowMeanings}
      aria-pressed={showMeanings}
      aria-label={label}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-white hover:bg-[#FAF7F0] text-[#1A2F4A] font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60 ${sizeClasses} ${className}`}>
      <Icon className="w-3.5 h-3.5 text-[#D4AF37]" aria-hidden="true" />
      {label}
    </button>
  );
}

/** Inline word + translation (used inside vocab lists, dialogues, grammar). */
export function MeaningInline({ fr, en, always = false, className = '', testid }) {
  const { showMeanings } = useLang();
  const visible = always || showMeanings;
  return (
    <span className={`inline-flex flex-col ${className}`} data-testid={testid}>
      <span className="text-[#1A2F4A]">{fr}</span>
      <span
        aria-live="polite"
        className={`text-[#6B4E37]/80 italic text-[0.85em] leading-snug transition-all duration-200 ${visible ? 'opacity-100 max-h-24 mt-0.5' : 'opacity-0 max-h-0 overflow-hidden'}`}>
        {en}
      </span>
    </span>
  );
}

/**
 * Block-level: French passage + English translation directly beneath.
 * Optionally provides its own local toggle (useLocalToggle=true).
 */
export function MeaningBlock({ fr, en, className = '', useLocalToggle = false, testid }) {
  const ctx = useLang();
  const [localOpen, setLocalOpen] = useState(false);
  const open = useLocalToggle ? localOpen : ctx.showMeanings;

  const toggle = useCallback(() => {
    if (useLocalToggle) setLocalOpen(o => !o);
    else ctx.toggleShowMeanings();
  }, [ctx, useLocalToggle]);

  return (
    <div className={`space-y-1 ${className}`} data-testid={testid}>
      <p className="text-[#1A2F4A]">{fr}</p>
      <p
        aria-live="polite"
        className={`text-[#6B4E37]/85 italic text-[0.9em] leading-relaxed transition-all duration-200 overflow-hidden ${open ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'}`}>
        {en}
      </p>
      {useLocalToggle && (
        <button
          type="button"
          onClick={toggle}
          aria-pressed={open}
          className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold hover:text-[#C9A961] mt-1 inline-flex items-center gap-1"
          data-testid={testid ? `${testid}-toggle` : 'meaning-block-toggle'}>
          <Languages className="w-3 h-3" />
          {open ? (ctx.lang === 'en' ? 'Hide meaning' : 'Masquer la traduction') : (ctx.lang === 'en' ? 'Show meaning' : 'Voir la traduction')}
        </button>
      )}
    </div>
  );
}

/**
 * Vocabulary list wrapper. Renders French words as primary content and
 * reveals the English meaning below each when the global toggle is on.
 *
 * Prop: items = [{ fr, en, note? }]
 * Optional: showButton (defaults true) — renders the MeaningToggleButton above the list
 */
export function VocabularyMeaningList({ items, showButton = true, gridClassName = 'grid sm:grid-cols-2 gap-3', className = '', testid = 'vocab-meaning-list' }) {
  const { showMeanings } = useLang();
  return (
    <div className={className} data-testid={testid}>
      {showButton && (
        <div className="flex justify-end mb-4">
          <MeaningToggleButton />
        </div>
      )}
      <div className={gridClassName} role="list">
        {items.map((v, i) => (
          <div
            key={v.fr + i}
            role="listitem"
            className="bg-white border border-[#E8E4DC] hover:border-[#D4AF37] rounded-xl px-4 py-3 transition-all"
            data-testid={`${testid}-item-${i}`}>
            <p className="text-[15px] font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {v.fr}
            </p>
            {v.note && (
              <p className="text-[12px] text-[#6B4E37] leading-relaxed mt-1">
                <span className="text-[#D4AF37] mr-1">→</span>
                {v.note}
              </p>
            )}
            <p
              aria-live="polite"
              className={`text-[12px] text-[#6B4E37]/80 italic leading-snug transition-all duration-200 overflow-hidden ${showMeanings ? 'opacity-100 max-h-16 mt-1.5' : 'opacity-0 max-h-0'}`}>
              <span className="text-[#D4AF37] mr-1 not-italic">✦</span>
              {v.en}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default VocabularyMeaningList;
